import React, { useRef, useMemo, useEffect } from 'react';
import { PropTypes, describe } from '@elliemae/ds-props-helpers';
import { DSIconColors } from '@elliemae/ds-icon';
import { RecentDocuments } from '@elliemae/ds-icons';
import { DSButton } from '@elliemae/ds-legacy-button-v1';
import { DSInputGroup } from '@elliemae/ds-legacy-form';
import { Input } from './Input.js';

const DSMobileTimePicker = ({ disabled, value, tabIndex, placeholder, onChange }) => {
  const inputRef = useRef(null);
  const valueArr = value.split(':');
  useEffect(() => {
    /*
      necessary if value is being set in mount, 
      otherwise the clear won't work on the first click
      [SEE input onChange comment]
    */
    if (inputRef.current) {
      inputRef.current.defaultValue = '';
    }
  }, [inputRef.current]);
  const displayValue = useMemo(() => {
    if (!value || !valueArr[0] || !valueArr[1]) return '';
    const ampm = valueArr[0] >= 12 ? 'pm' : 'am';
    const h = valueArr[0] > 12 ? valueArr[0] - 12 : valueArr[0];

    return `${h} : ${valueArr[1]} ${ampm}`;
  }, [value]);

  return (
    <DSInputGroup
      rightAddon={
        <DSButton
          aria-label="Time Picker Button"
          color={!disabled ? DSIconColors.PRIMARY : DSIconColors.NEUTRAL}
          disabled={disabled}
          icon={<RecentDocuments />}
          type="button"
          buttonType="secondary"
          onClick={() => {
            inputRef.current.focus();
            inputRef.current.click();
          }}
        />
      }
    >
      <Input
        innerRef={inputRef}
        type="time"
        data-testid="ds-mobile-time-picker"
        aria-label="Time Picker Input Mask"
        disabled={disabled}
        placeholder={placeholder}
        tabIndex={tabIndex}
        value={value}
        data-displayvalue={displayValue}
        onChange={(e) => {
          // https://github.com/facebook/react/issues/8938#issuecomment-360573204
          const { target } = e.nativeEvent;
          function iosClearDefault() {
            target.defaultValue = '';
          }
          window.setTimeout(iosClearDefault, 0);
          onChange(e);
        }}
      />
    </DSInputGroup>
  );
};

DSMobileTimePicker.defaultProps = {
  disabled: false,
  tabIndex: 0,
  placeholder: 'HH : MM am',
  value: '',
};

const pickerProps = {
  /**
   * defaults to false
   */
  disabled: PropTypes.bool.description('disabled prop, defaults to false'),
  /**
   * 0 as default
   */
  tabIndex: PropTypes.number.description('focus order HTML property, 0 as default'),
  /**
   * HH : MM am as default
   */
  placeholder: PropTypes.string.description('HH : MM am as default'),
  /**
   * onChange handler, receives change event as first argument
   */
  onChange: PropTypes.func.description('onChange handler, receives change event as first argument'),
  /**
   * HH:MM string representing the time or empty string
   */
  value: PropTypes.string.description('HH:MM string representing the time or empty string'),
};

DSMobileTimePicker.displayName = 'DSMobileTimePicker';
const DSMobileTimePickerWithSchema = describe(DSMobileTimePicker).description('Time picker for mobile & tablet');

DSMobileTimePickerWithSchema.propTypes = pickerProps;

export { DSMobileTimePicker, DSMobileTimePickerWithSchema };
